/**
 * Copyright 2021 Felix Martinez
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"),to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
 * ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { Pane } from 'tweakpane'
import { AmbientLight, BoxBufferGeometry, CameraHelper, Color, Fog, LoaderUtils, Mesh, MeshBasicMaterial, MeshNormalMaterial, MeshPhongMaterial, PCFSoftShadowMap, PerspectiveCamera, PlaneBufferGeometry, SphereBufferGeometry, SpotLight, SpotLightShadow, Vector3 } from 'three'
import ThreeBase from '../core/ThreeBase'
import GPURendering from '../gpu/sim/GPURendering'
import GPUSimulation from '../gpu/sim/GPUSimulation'
import { ImprovedNoise } from '../../utils/ImprovedNoise'
import CameraControl from '../../utils/CameraControl'
import Vignette from '../../utils/Vignette'
import Atmosphere from '../../utils/Atmosphere'
// import ShadowMapViewer from '../../utils/ShadowMapViewer'

class MainScene extends ThreeBase {
  private rafHandler: any
  private raf?: number
  private controls?: CameraControl
  private debug: boolean = true
  private gpuSimulation?: GPUSimulation
  private gpuRendering?: GPURendering
  private pane?: Pane
  private offSetCamera: Vector3 = new Vector3(0, 0, -1250)
  private cameraTarget: Vector3 = new Vector3(0, 0,0)
  private lightTarget: Vector3 = new Vector3(0, 0, 0)
  private light: SpotLight
  // private lightShadowMapViewer: ShadowMapViewer
  private debMesh: Mesh

  constructor(width: number, height: number, container: Element) {
    super(width, height, container)
    this.rafHandler = this.update.bind(this)
    this.events()
    this.setupControls()
    this.setup()
  }

  public resize() {
    this.width = window.innerWidth
    this.height = window.innerHeight

    this.renderer.setSize(this.width, this.height)
    this.camera.aspect = this.width / this.height
    this.camera.updateProjectionMatrix()
  }

  private events() {
    window.addEventListener('visibilitychange', () => {
      this.clock.getDelta()
    })
  }

  private update(): void {
    this.raf = requestAnimationFrame(this.rafHandler)

    const dt = this.clock.getDelta()
    // this.debMesh.position.y = Math.abs(Math.sin(this.clock.getElapsedTime())) * 200
    // const scaleNoise = 200
    // const x = ImprovedNoise().noise(
    //   this.clock.getElapsedTime() * 0.1,
    //   this.clock.getElapsedTime() * 0.1,
    //   this.clock.getElapsedTime() * 0.1
    // ) * scaleNoise
    // const y = ImprovedNoise().noise(
    //   this.clock.getElapsedTime() * -0.1,
    //   this.clock.getElapsedTime() * -0.1,
    //   this.clock.getElapsedTime() * 0.1
    // ) * scaleNoise
    // const z = ImprovedNoise().noise(
    //   this.clock.getElapsedTime() * -0.1,
    //   this.clock.getElapsedTime() * 0.1,
    //   this.clock.getElapsedTime() * -0.1
    // ) * scaleNoise

    this.controls.target.x = -200
    // this.controls.target.y = y * 5 + 100
    this.controls.target.z -= dt * 50
    // this.offSetCamera.z -= 10
    this.light.target.position.z -= dt * 50
    this.light.position.z -= dt * 50
    this.debMesh.position.z -= dt * 50
    this.controls?.update(this.clock.getElapsedTime())
    this.gpuSimulation?.update(dt)
    this.gpuRendering?.update()
    // this.gpuRendering!.position.z -= dt * 10
    
    this.render()
    // this.lightShadowMapViewer.render(this.renderer)
  }

  private setup(): void {
    const colorPaletteCoord = (window as any).fxrand()
    this.canvasExtractionFogColor(colorPaletteCoord).then(() => {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = PCFSoftShadowMap;
      this.renderer.extensions.get( 'EXT_color_buffer_float' );
      
      this.gpuSimulation = new GPUSimulation(800*80, this.renderer, this.controls.target, this.clock)
      this.gpuRendering = new GPURendering(this.gpuSimulation, this.clock, new BoxBufferGeometry(1.75, 1.75, 1.75, 1, 1, 1), colorPaletteCoord)
      this.light = new SpotLight( 0xffffff, 1.0 )
      this.light.position.set(0, 600, -800)
      this.light.castShadow = true
      this.light.penumbra = 1
      this.light.shadow.camera.near = 800
      this.light.shadow.camera.far = 1400
      this.light.shadow.camera.fov = 90
      this.light.shadow.bias = 0.0
      this.light.shadow.mapSize.width = 2048
      this.light.shadow.mapSize.height = 2048
      this.scene.add( this.light.target );

      this.debMesh = new Mesh(new PlaneBufferGeometry(10000, 10000, 1, 1), new MeshPhongMaterial({color: 0xc1a994}))
      this.debMesh.rotation.x = -Math.PI / 2
      this.debMesh.position.y -= 0.2
      this.debMesh.receiveShadow = true

      // this.scene.add(  new CameraHelper( this.light.shadow.camera ) );

      // this.lightShadowMapViewer = new ShadowMapViewer( this.light );
      // this.lightShadowMapViewer.position.x = 0
      // this.lightShadowMapViewer.position.y = 0
      // this.lightShadowMapViewer.size.width = 64
      // this.lightShadowMapViewer.size.height = 64
      // this.lightShadowMapViewer.update()
        
      this.scene.add(this.light)
      this.scene.add(new AmbientLight(0xffffff, 0.3))
      // this.scene.add(this.gpuSimulation)
      this.scene.add(this.gpuRendering)
      this.scene.add(this.debMesh)
      // this.scene.add(new Vignette())
      // this.scene.add(new Atmosphere())

      // this.setupGui()
      const t = (window as any).fxpreview()

      setTimeout(()=>this.update(), 500)
    })
  }

  private canvasExtractionFogColor(colorPaletterCoord: number): Promise<void> {
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = ''
      const canvas: HTMLCanvasElement = document.createElement('canvas')
      canvas.width = 5
      canvas.height = 24
      const ctx = canvas.getContext('2d')
      img.onload = () => {
        ctx.drawImage(img, 0, 0)
        img.style.display = 'none'
        var pixel = ctx.getImageData(0, (1-colorPaletterCoord) * canvas.height, 1, 1);
        var data = pixel.data;
        const c = new Color(0x232323)
        const fog = new Fog(c, 1800, 4200)
        this.scene.fog = fog
        this.renderer.setClearColor(c)
        resolve()
      }
      img.src = './assets/color_palette.png'
    })
  }
  private setupControls(): void {
    this.controls = new CameraControl(this.camera, this.cameraTarget, this.renderer.domElement)
  }

  private setupGui() {
    const pane = new Pane({ expanded: true })
    pane.element.parentElement!.style.display = 'none'
    this.pane = pane

    // pane.addInput(this.bloomPass!, 'strength', {
    //   min: 0,
    //   max: 10,
    //   step: 0.001,
    // })

    // pane.addInput(this.bloomPass!, 'radius', {
    //   min: 0,
    //   max: 2,
    //   step: 0.001,
    // })

    // pane.addInput(this.bloomPass!, 'threshold', {
    //   min: 0,
    //   max: 2,
    //   step: 0.001,
    // })

    window.addEventListener('dblclick', () => {
      if (this.debug) {
        pane.element.parentElement!.style.display = 'block'
      } else {
        pane.element.parentElement!.style.display = 'none'
      }
      this.debug = !this.debug
    })
  }
}

export default MainScene
